import React, { useEffect, useState } from 'react'
import { groupBy } from 'lodash'

// Components
import CompetitorEventRegisterWrapper from '../../CompetitorEventRegisterWrapper'
import DataNotAvailable from '../../../../components/common/alerts/data-not-available/DataNotAvailable'
import { TicketsColumn } from '../../../../components/common/TicketsColumn'
import { Skeleton } from '../../../../components/ui/skeleton'

import { useAppSelector } from '../../../../store/hooks'
import {
  selectRegistrationTicketsR,
  selectTicketTabData,
} from '../../../../store/registration/registrationSlice'

import { selectedEvent } from '../../../../store/events/eventsSlice'
import { getCurrency } from '../../../../helpers/payment'

import useRegistrationTickets from '../../../../hooks/useRegistrationTickets'

import RegistrationTicketService from '../../../../services/firebase-service/registration-tickets/RegistrationTicketService'
import AssignedTicketsService from '../../../../services/firebase-service/assigned-tickets/AssignedTicketsService'

import { ITicketTab } from '../../../../models/event-registered-users/event-registered-users.interface'
import { IAssignedTicket } from '../../../../models/assigned-tickets/assigned-tickets.interface'
import { IRegistrationTicketInterface } from '../../../../models/registration-tickets/registrationTicket.interface'

type CompetitorEventRegisterTicketsTabProps = {
  title: string
  description: string
  eventId: string
}

const CompetitorEventRegisterTicketsTab = ({
  title,
  description,
  eventId,
}: CompetitorEventRegisterTicketsTabProps) => {
  const { onChangeTicket } = useRegistrationTickets()

  const ticketTabData = useAppSelector(selectTicketTabData)
  const selectedEventR = useAppSelector(selectedEvent)
  const registrationTickets = useAppSelector(selectRegistrationTicketsR)

  const [loading, setLoading] = useState(false)
  const [mainLoading, setMainLoading] = useState(false)
  const [soldTicketsById, setSoldTicketsById] = useState<{
    [key: string]: (IRegistrationTicketInterface | IAssignedTicket)[]
  }>({})

  const currency = getCurrency(selectedEventR?.EventPaymentSettings?.currency)

  const onBlur = async (ticket: ITicketTab, value: number) => {
    setLoading(true)
    await onChangeTicket({
      currentRow: ticket,
      selectedCount: Number(value),
      publishedEvent: selectedEventR,
    })
    setLoading(false)
  }

  useEffect(() => {
    const getTicketsInDb = async () => {
      try {
        setMainLoading(true)
        const eventTicketsInDb = await RegistrationTicketService.getByEventId(eventId)
        const assignedTicketsInDb = await AssignedTicketsService.getByEventId(eventId)
        const grouped = groupBy([...assignedTicketsInDb, ...eventTicketsInDb], 'ticketItemId')
        setSoldTicketsById(grouped)
      } catch (error) {
        console.error('error: ', error)
      } finally {
        setMainLoading(false)
      }
    }
    getTicketsInDb().then()
  }, [eventId])

  if (mainLoading) {
    return (
      <CompetitorEventRegisterWrapper title={title} description={description} className="mt-0">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="flex flex-wrap items-center gap-y-2 justify-between w-full py-4"
          >
            <div className="w-full sm:w-1/2">
              <Skeleton className="h-5 w-1/4" />
              <Skeleton className="h-4 w-1/2 mt-1" />
            </div>
            <div className="w-full sm:w-1/2">
              <Skeleton className="ml-auto h-10 w-full sm:w-[160px]" />
            </div>
          </div>
        ))}
      </CompetitorEventRegisterWrapper>
    )
  }
  return (
    <CompetitorEventRegisterWrapper title={title} description={description} className="mt-0">
      <div id="rtc">
        {ticketTabData?.[0] ? (
          ticketTabData.map((ticket, index) => {
            const defaultValue = registrationTickets.filter(
              (fee) => fee.ticketItemId === ticket.ticketItemId
            )

            return (
              <TicketsColumn
                key={index}
                loading={loading}
                currency={currency}
                name={ticket.ticketTitle}
                price={ticket.ticketPrice}
                available={ticket.available - (soldTicketsById[ticket.ticketItemId]?.length || 0)}
                description={ticket.ticketDescription}
                onBlur={(value) => onBlur(ticket, value)}
                defaultValue={defaultValue.length}
              />
            )
          })
        ) : (
          <DataNotAvailable
            mode="text"
            containerClassName="text-SeabiscuitDark200ThemeColor text-nr 2xl:text-base"
            text="No ticket items have been added to this event"
          />
        )}
      </div>
    </CompetitorEventRegisterWrapper>
  )
}

export default CompetitorEventRegisterTicketsTab
